"use client";

import Image from "next/image";
import Link from "@/components/ui/CcLink";
import footerCopy from "@/data/footer-copy.generated.json";
import {
  CcClosingAtmosphere,
  CcClosingBrandGlow,
} from "@/components/layout/CcClosingAtmosphere";
import { CcLangSwitcher } from "@/components/layout/CcLangSwitcher";
import { useSiteLocale } from "@/components/layout/CcLocaleContext";
import type { NavResourceMegaColumn } from "@/lib/cc-nav-resources-mega";
import type { NavServiceMegaColumn } from "@/lib/cc-nav-services-mega";
import { t } from "@/lib/content";
import { devSectionAttr } from "@/lib/dev/section-attr";
import type { Lang } from "@/lib/types";
import {
  CC_FOOTER_LAYOUT,
  isOverviewAttachedFooter,
  type CcFooterLayout,
} from "@/lib/cc-footer-layout";

type CcFooterProps = {
  links: { label: string; href: string }[];
  serviceLinks: { label: string; href: string }[];
  serviceMegaColumns: NavServiceMegaColumn[];
  industriesMegaColumn: NavResourceMegaColumn | null;
  privacyHref: string;
  /** Drives CcNavbar when the nav line is over the footer dark surface (`__frame`). */
  navTheme?: "dark" | "light";
  continuesClosingBand?: boolean;
  /** Antique ruins footer photo; false on `/contact` and 404. */
  useAntiquePhoto?: boolean;
  layout?: CcFooterLayout;
};

function footerClassName(
  continuesClosingBand: boolean,
  useAntiquePhoto: boolean,
  layout: CcFooterLayout,
  hasClosingBand: boolean,
): string {
  const classes = ["cc-footer"];

  if (continuesClosingBand) {
    classes.push("cc-footer--continues-closing");
  }

  if (isOverviewAttachedFooter(layout)) {
    classes.push("cc-footer--attached");
  }

  if (hasClosingBand) {
    classes.push("cc-footer--with-closing-band");
  }

  if (useAntiquePhoto) {
    classes.push("cc-footer--photo");
  } else {
    classes.push("cc-footer--no-photo");
  }

  if (isOverviewAttachedFooter(layout) && !useAntiquePhoto) {
    classes.push("cc-footer--flat");
  }

  return classes.join(" ");
}

type FooterMegaLink = { key: string; label: string; href: string };

function FooterMegaColumn({
  header,
  headerHref,
  links,
}: {
  header: string;
  headerHref: string;
  links: FooterMegaLink[];
}) {
  return (
    <div className="cc-footer__services-col">
      <h2 className="cc-footer__heading">
        <Link href={headerHref} className="cc-footer__heading-link">
          {header}
        </Link>
      </h2>
      <ul className="cc-footer__links">
        {links.map((link) => (
          <li key={link.key}>
            <Link href={link.href}>{link.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CcFooter({
  links,
  serviceLinks,
  serviceMegaColumns,
  industriesMegaColumn,
  privacyHref,
  navTheme,
  continuesClosingBand = false,
  useAntiquePhoto = true,
  layout = CC_FOOTER_LAYOUT.articleDetached,
}: CcFooterProps) {
  const locale = useSiteLocale();
  const lang = locale as Lang;
  const navigationLinks = [
    ...links,
    {
      label: t(footerCopy.privacyPolicy, lang),
      href: privacyHref,
    },
  ].filter((link) => lang === "en" || !link.href.startsWith("/") || link.href === `/${lang}` || link.href.startsWith(`/${lang}/`));

  const footerMegaColumns = serviceMegaColumns.filter(
    (column) => column.links.length > 0,
  );

  const hasClosingBand = false;

  const overviewAttached = isOverviewAttachedFooter(layout);
  const showInlineAtmosphere = useAntiquePhoto && !continuesClosingBand;
  const attachedFooterAtmosphere =
    overviewAttached && showInlineAtmosphere;
  const flatAttachedSurface = overviewAttached && !useAntiquePhoto;
  const atmosphereVariant = attachedFooterAtmosphere ? "attached-footer" : "footer";

  const footerNavThemeAttr =
    navTheme === "dark" && hasClosingBand
      ? ({ "data-cc-nav": "dark" as const })
      : {};

  return (
    <footer
      className={footerClassName(
        continuesClosingBand,
        useAntiquePhoto,
        layout,
        hasClosingBand,
      )}
      {...footerNavThemeAttr}
      {...devSectionAttr("footer")}
    >
      <div
        className="cc-footer__frame"
        {...(navTheme ? { "data-cc-nav": navTheme } : {})}
      >
        {showInlineAtmosphere ? (
          <CcClosingAtmosphere variant={atmosphereVariant} />
        ) : null}
        {flatAttachedSurface ? <CcClosingBrandGlow /> : null}
        {flatAttachedSurface || attachedFooterAtmosphere ? null : (
          <div className="cc-dark-frame__backdrop" aria-hidden="true">
            <div className="cc-dark-frame__glow cc-dark-frame__glow--teal" />
            <div className="cc-dark-frame__glow cc-dark-frame__glow--violet" />
            <div className="cc-dark-frame__pattern" />
          </div>
        )}
        <div className="cc-container">
          <div className="cc-footer__body">
            <div className="cc-footer__brand">
              <Image
                src="/assets/imgs/CaptivateClick-logo.svg"
                alt="CaptivateClick"
                width={200}
                height={40}
              />
            </div>

            <div className="cc-footer__grid">
              <div className="cc-footer__link-col">
                <h2 className="cc-footer__heading">{t(footerCopy.navigationTitle, lang)}</h2>
                <ul className="cc-footer__links">
                  {navigationLinks.map((link) => (
                    <li key={link.href}>
                      <Link href={link.href}>{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="cc-footer__link-col">
                <h2 className="cc-footer__heading">{t(footerCopy.servicesTitle, lang)}</h2>
                <ul className="cc-footer__links">
                  {serviceLinks.map((link) => (
                    <li key={link.href}>
                      <Link href={link.href}>{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              {footerMegaColumns.map((column) => (
                <FooterMegaColumn
                  key={column.tag}
                  header={column.header}
                  headerHref={column.headerHref}
                  links={column.links}
                />
              ))}
              {industriesMegaColumn && industriesMegaColumn.links.length > 0 ? (
                <FooterMegaColumn
                  key={industriesMegaColumn.key}
                  header={industriesMegaColumn.header}
                  headerHref={industriesMegaColumn.headerHref}
                  links={industriesMegaColumn.links}
                />
              ) : null}
            </div>
          </div>

          <div className="cc-footer__bottom">
            <span>{t(footerCopy.copyright, lang)}</span>
            <div className="cc-footer__bottom-end">
              <CcLangSwitcher className="cc-footer__lang" variant="popover" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
